import { useEffect, useRef } from 'react';
import { useMenuCacheUtils } from '@/services/menu/menuItem/fetchMenuItems';

interface UseMenuCacheManagerProps {
  storeId: string | undefined;
  autoRefreshInterval?: number; // in milliseconds, default 1 hour
  enableBackgroundRefresh?: boolean;
}

export const useMenuCacheManager = ({
  storeId,
  autoRefreshInterval = 60 * 60 * 1000, // 1 hour default
  enableBackgroundRefresh = true,
}: UseMenuCacheManagerProps) => {
  const { prefetchMenuItems, prefetchCategories, clearMenuCache } = useMenuCacheUtils();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(Date.now());

  // Prefetch data when store changes
  useEffect(() => {
    if (!storeId) return;

    const prefetchData = async () => {
      try {
        await Promise.all([
          prefetchMenuItems(storeId),
          prefetchCategories(storeId),
        ]);
        lastRefreshRef.current = Date.now();
        console.log(`Menu cache prefetched for store: ${storeId}`);
      } catch (error) {
        console.error('Error prefetching menu data:', error);
      }
    };

    prefetchData();
  }, [storeId, prefetchMenuItems, prefetchCategories]);

  // Set up automatic refresh interval
  useEffect(() => {
    if (!storeId || !enableBackgroundRefresh) return;

    const refreshData = async () => {
      try {
        await Promise.all([
          prefetchMenuItems(storeId),
          prefetchCategories(storeId),
        ]);
        lastRefreshRef.current = Date.now();
        console.log(`Menu cache refreshed for store: ${storeId}`);
      } catch (error) {
        console.error('Error refreshing menu data:', error);
      }
    };

    intervalRef.current = setInterval(refreshData, autoRefreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [storeId, autoRefreshInterval, enableBackgroundRefresh, prefetchMenuItems, prefetchCategories]);

  // Clear cache when component unmounts or store changes
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Background refresh when tab becomes visible
  useEffect(() => {
    if (!storeId || !enableBackgroundRefresh) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        
        // Refresh if more than 30 minutes have passed since last refresh
        if (timeSinceLastRefresh > 30 * 60 * 1000) {
          try {
            await Promise.all([
              prefetchMenuItems(storeId),
              prefetchCategories(storeId),
            ]);
            lastRefreshRef.current = Date.now();
            console.log(`Menu cache refreshed on tab focus for store: ${storeId}`);
          } catch (error) {
            console.error('Error refreshing menu data on tab focus:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [storeId, enableBackgroundRefresh, prefetchMenuItems, prefetchCategories]);

  return {
    lastRefreshTime: lastRefreshRef.current,
    clearCache: () => {
      if (storeId) {
        clearMenuCache(storeId);
        lastRefreshRef.current = Date.now();
      }
    },
  };
}; 